import { Spin } from "antd";
import i18n from "i18next";
import type { FC } from "react";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router";

import type { AidPreferenceQueryParams } from "src/app/features/EmvKernelProfile/subFeatures/AidPreference/services/server/AidPreferenceQueryTypes";
import { FormManager } from "../../../../../../../components/Form/FormManager";
import { useLoadFormDefinitionQuery } from "../../../../../../../components/Form/services/server/serverApi";
import { FormLayoutType } from "../../../../../../../components/Form/types";
import type { FormProps } from "../../../../../../../components/Form/types/FormProps";
import { ResourceName, ResourceVariant } from "../../../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../../../core/router/types/routerTypes";
import { showSuccessNotification } from "../../../../../../../core/setup/redux/state/notifications/notificationsState";
import { useAidPreferenceUpdateMutation, useLazyAidPreferenceLoadQuery } from "../../services/server/AidPreferenceApi";
import type { AidPreferenceEntity } from "../../types/AidPreferenceEntity";

export const AidPreferenceEditForm: FC<FormProps> = ({ runSubmit }) => {
    const navigate = useNavigate();
    const { emvKernelProfileId, id } = useParams();
    const [queryConfig, setQueryConfig] = useState<AidPreferenceQueryParams>({});

    // set save params
    useEffect(() => {
        setQueryConfig({ emvKernelProfileId, id });
    }, [emvKernelProfileId, id]);

    // load form and data
    const { data: formDefinition, isFetching: configIsFetching } = useLoadFormDefinitionQuery("aid-preference");
    const [loadFormData, { isFetching: loadFormIsInProgress }] = useLazyAidPreferenceLoadQuery();

    const [formData, setFormData] = useState<AidPreferenceEntity>({} as AidPreferenceEntity);
    const onChangeCallback = useCallback(setFormData, [setFormData]);

    // load form data
    useEffect(() => {
        if (id && queryConfig.emvKernelProfileId) {
            loadFormData(queryConfig).then((loadedData) => {
                if (loadedData.data) {
                    setFormData(loadedData.data);
                }
            });
        }
    }, [id, loadFormData, queryConfig]);

    const [saveFormData, { isLoading: saveFormIsInProgress }] = useAidPreferenceUpdateMutation();
    const dispatch = useDispatch();
    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formData) {
                return;
            }

            const result = await saveFormData({ aidPreferenceFormData: formData, aidPreferenceQuery: queryConfig });

            const hasError = "error" in result;
            if (!hasError) {
                dispatch(
                    showSuccessNotification({
                        text: i18n.t("common.formUpdateSuccess", {
                            id: result.data.id,
                            resource: "AidPreference",
                        }),
                        autoCloseDisabled: false,
                    }),
                );
                navigate(
                    `${BASE_PATH}/${ResourceName.EMV_KERNEL_PROFILES}/${ResourceVariant.APAC}/${FORM_ACTION.EDIT}/${emvKernelProfileId}`,
                );
            }
        },
        [formData, saveFormData, queryConfig, dispatch, navigate, emvKernelProfileId],
    );

    return (
        <Spin spinning={saveFormIsInProgress || configIsFetching || loadFormIsInProgress}>
            {formDefinition?.formConfiguration && (
                <FormManager<AidPreferenceEntity>
                    name="AidPreferenceEditForm"
                    formData={formData}
                    formLayout={FormLayoutType.TABS}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
        </Spin>
    );
};
